import { initializeApp } from "firebase/app";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, collection, query, orderBy, limit, getDocs, Timestamp, updateDoc, deleteDoc } from "firebase/firestore";
import axios from "axios";

// API URL'yi environment'tan al
const API_URL = process.env.REACT_APP_API_URL || 'https://api-got6faywaa-uc.a.run.app';

// Firebase konfigürasyonu
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

export const getIdToken = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    // Token almak için birkaç deneme yap
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        const token = await user.getIdToken(true); // force refresh
        return token;
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          console.error("Token alınamadı, maksimum deneme sayısına ulaşıldı:", error);
          throw error;
        }
        // Kısa bir bekleme süresi ekle
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return null;
  } catch (error) {
    console.error("getIdToken hatası:", error);
    return null;
  }
};

export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    
    // Gerekli izinleri ekle
    provider.addScope('https://www.googleapis.com/auth/spreadsheets');
    provider.addScope('profile');
    provider.addScope('email');
    
    // Özel parametreleri ayarla
    provider.setCustomParameters({
      prompt: 'select_account',
      access_type: 'offline'
    });
    
    // Önce auth durumunu kontrol et
    if (!auth.currentUser) {
      // Popup ile giriş yapmayı dene
      try {
        const result = await signInWithPopup(auth, provider);
        
        if (!result || !result.user) {
          throw new Error("Kullanıcı bilgileri alınamadı");
        }
        
        // Kullanıcı dokümanını oluştur veya güncelle
        try {
          const userDocRef = doc(db, "users", result.user.uid);
          await setDoc(userDocRef, {
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL,
            lastLogin: Timestamp.now(),
            isAdmin: false
          }, { merge: true });
        } catch (dbError) {
          console.error("Kullanıcı verileri kaydedilirken hata:", dbError);
          // Veritabanı hatası olsa bile giriş başarılı sayılabilir
        }

        return result;
      } catch (popupError) {
        console.error("Popup ile giriş yapılırken hata:", popupError);
        
        // Popup hatası durumunda yeniden dene veya hata fırlat
        if (popupError.code === 'auth/popup-closed-by-user' || 
            popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/cancelled-popup-request') {
          throw popupError; // Bu hataları doğrudan fırlat
        }
        
        throw new Error("Giriş yapılırken bir hata oluştu: " + popupError.message);
      }
    } else {
      // Zaten giriş yapmış
      return { user: auth.currentUser };
    }
  } catch (error) {
    console.error("Google ile giriş yapılırken hata:", error);
    
    // Kullanıcı dostu hata mesajları
    let errorMessage = "Giriş yapılırken bir hata oluştu.";
    
    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = "Giriş penceresi kapatıldı. Lütfen tekrar deneyin.";
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = "Tarayıcınız popup penceresini engelledi. Lütfen popup izinlerini kontrol edin.";
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = "Giriş işlemi iptal edildi. Lütfen tekrar deneyin.";
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = "Ağ hatası. İnternet bağlantınızı kontrol edin.";
    }
    
    // Özelleştirilmiş hata mesajı ile hatayı fırlat
    const customError = new Error(errorMessage);
    customError.originalError = error;
    throw customError;
  }
};

export const checkAdminStatus = async (uid) => {
  try {
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const userData = docSnap.data();
      return userData.isAdmin === true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const refreshAdminStatus = async (user, setIsAdmin) => {
  if (!user) return false;
  
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);
    const isAdmin = userDoc.exists() && userDoc.data().isAdmin;
    
    if (setIsAdmin) {
      setIsAdmin(isAdmin);
    }
    
    return isAdmin;
  } catch (error) {
    return false;
  }
};

// Sorgu geçmişini veritabanına kaydetme
export const saveQueryToHistory = async (userId, queryData) => {
  try {
    const userHistoryRef = doc(db, `users/${userId}/queryHistory`, queryData.domain);
    await setDoc(userHistoryRef, queryData, { merge: true });
  } catch (error) {
    console.error("Error saving query to history:", error);
  }
};

// Sorgu geçmişini veritabanından getirme
export const getQueryHistory = async (userId) => {
  try {
    const userHistoryRef = collection(db, `users/${userId}/queryHistory`);
    const q = query(userHistoryRef, orderBy("timestamp", "desc"), limit(100));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      ...doc.data(),
      domain: doc.id
    }));
  } catch (error) {
    console.error("Error getting query history:", error);
    return [];
  }
};

export const saveQueryHistoryVisibility = async (userId, isVisible) => {
  try {
    await setDoc(doc(db, `users/${userId}`), { isQueryHistoryVisible: isVisible }, { merge: true });
  } catch (error) {
    console.error("Error saving query history visibility:", error);
  }
};

export const getQueryHistoryVisibility = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, `users/${userId}`));
    if (userDoc.exists()) {
      return userDoc.data().isQueryHistoryVisible ?? true; // Varsayılan olarak görünür
    }
    return true;
  } catch (error) {
    console.error("Error getting query history visibility:", error);
    return true;
  }
};

export const saveDomainData = async (userId, domain, data) => {
  try {
    const cleanData = JSON.parse(JSON.stringify(data));
    await setDoc(doc(db, `users/${userId}/domains`, domain), {
      name: domain,
      ...cleanData,
      lastUpdated: Timestamp.now()
    }, { merge: true });
  } catch (error) {
    console.error("Error saving domain data:", error);
  }
};

export const getDomainData = async (userId, domain) => {
  try {
    const docRef = doc(db, `users/${userId}/domains`, domain);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const lastUpdated = data.lastUpdated?.toDate();
      const now = new Date();
      if (!lastUpdated || now - lastUpdated > 5 * 60 * 1000) { // 5 dakikadan eski ise veya lastUpdated yoksa
        return null; // Yeni sorgulama yapılması gerekiyor
      }
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error getting domain data:", error);
    return null;
  }
};

// Dark mode tercihini kaydet
export const saveDarkModePreference = async (uid, isDarkMode) => {
  try {
    const userDocRef = doc(db, "users", uid);
    await updateDoc(userDocRef, {
      darkMode: isDarkMode
    });
    return true;
  } catch (error) {
    console.error("Dark mode tercihi kaydedilemedi:", error);
    return false;
  }
};

// Dark mode tercihini getir
export const getDarkModePreference = async (uid) => {
  try {
    const userDocRef = doc(db, "users", uid);
    const userDoc = await getDoc(userDocRef);
    return userDoc.exists() ? userDoc.data().darkMode : true; // Varsayılan olarak dark mode açık
  } catch (error) {
    console.error("Dark mode tercihi getirilemedi:", error);
    return true; // Hata durumunda varsayılan olarak dark mode açık
  }
};

// Rate limiting için Firestore koleksiyonu
const rateLimit = {
  async checkRateLimit(userId) {
    const userRateLimitRef = doc(db, 'rateLimits', userId);
    const now = Date.now();

    try {
      const docSnap = await getDoc(userRateLimitRef);
      let rateData = docSnap.exists() ? docSnap.data() : {
        queryCount: 0,
        lastQuery: now,
        hourlyCount: 0,
        lastHourReset: now
      };

      // Saatlik limit kontrolü
      if (now - rateData.lastHourReset > 3600000) {
        rateData.hourlyCount = 0;
        rateData.lastHourReset = now;
      }

      // Dakikalık limit kontrolü
      if (now - rateData.lastQuery > 60000) {
        rateData.queryCount = 0;
      }

      // Limitleri kontrol et
      if (rateData.queryCount >= 30) { // Dakikada 30 sorgu
        throw new Error('RATE_LIMIT_EXCEEDED_MINUTE');
      }
      if (rateData.hourlyCount >= 500) { // Saatte 500 sorgu
        throw new Error('RATE_LIMIT_EXCEEDED_HOUR');
      }

      // Sayaçları güncelle
      rateData.queryCount++;
      rateData.hourlyCount++;
      rateData.lastQuery = now;

      // Firestore'u güncelle
      await setDoc(userRateLimitRef, rateData);
      return true;

    } catch (error) {
      if (error.message === 'RATE_LIMIT_EXCEEDED_MINUTE') {
        throw new Error('Dakikalık sorgu limitine ulaştınız. Lütfen bir dakika bekleyin.');
      }
      if (error.message === 'RATE_LIMIT_EXCEEDED_HOUR') {
        throw new Error('Saatlik sorgu limitine ulaştınız. Lütfen bir saat sonra tekrar deneyin.');
      }
      throw error;
    }
  }
};

export const handleQuery = async (domain, queryType, idToken) => {
  try {
    // Rate limit kontrolü - sadece kullanıcı giriş yapmışsa
    if (auth.currentUser) {
      try {
        await rateLimit.checkRateLimit(auth.currentUser.uid);
      } catch (rateLimitError) {
        // Rate limit uyarısını iletelim
        throw new Error(rateLimitError.message);
      }
    }

    // API isteği için headers
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Eğer token varsa kullan
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await axios.post(
      `${API_URL}/api/dns`,
      { domain, queryType },
      { headers }
    );
    return response.data;
  } catch (error) {
    // Hata detaylarını logla
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Sorguyu kaydetme
export const saveQuery = async (userId, queryData) => {
  try {
    // Timestamp objelerini düzleştirme yardımcı fonksiyonu
    const flattenTimestamps = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      // Yeni bir obje oluştur, orijinali değiştirme
      const newObj = Array.isArray(obj) ? [...obj] : {...obj};
      
      for (const key in newObj) {
        // Firestore Timestamp objesi kontrolü
        if (newObj[key] && typeof newObj[key] === 'object' && 
            newObj[key].seconds !== undefined && newObj[key].nanoseconds !== undefined) {
          try {
            // Timestamp'i Date'e çevir
            newObj[key] = new Date(newObj[key].seconds * 1000);
          } catch (e) {
            // Hata durumunda şu anki zamanı kullan
            newObj[key] = new Date();
          }
        } 
        // Nested objeleri recursive olarak kontrol et
        else if (newObj[key] && typeof newObj[key] === 'object') {
          newObj[key] = flattenTimestamps(newObj[key]);
        }
      }
      
      return newObj;
    };
    
    // Sorgu tipine göre veriyi temizle
    let cleanData = {
      domain: queryData.domain || null,
      queryType: queryData.queryType || null,
      timestamp: Timestamp.fromDate(queryData.timestamp || new Date()),
      lastChecked: Timestamp.fromDate(queryData.lastChecked || new Date())
    };

    // Gmail sorgusu için özel işlemler
    if (queryData.queryType === 'gmail') {
      // DMARC için uyarı kontrolü
      if (queryData.dmarc && queryData.dmarc.record && queryData.dmarc.record.toLowerCase().includes('p=none')) {
        queryData.dmarc.message = 'UYARI: DMARC politikası "none" olarak ayarlanmış. Bu, e-posta güvenliğinizi zayıflatabilir.';
        queryData.dmarc.valid = false;
      }

      // MX için eski kayıt kontrolü
      if (queryData.mx && queryData.mx.records) {
        const hasOldMxFormat = queryData.mx.records.some(record => 
          record.toLowerCase().includes('aspmx.l.google.com') || 
          record.toLowerCase().includes('alt1.aspmx.l.google.com') ||
          record.toLowerCase().includes('alt2.aspmx.l.google.com')
        );
        
        if (hasOldMxFormat) {
          queryData.mx.message = 'UYARI: Eski format MX kayıtları kullanılıyor. Yeni "smtp.google.com" formatına geçmeniz önerilir.';
          queryData.mx.valid = false;
        }
      }

      cleanData = {
        ...cleanData,
        mx: queryData.mx ? {
          status: queryData.mx.valid ? 'success' : queryData.mx.message?.includes('UYARI') ? 'warning' : 'error',
          message: queryData.mx.message || null,
          records: Array.isArray(queryData.mx.records) ? queryData.mx.records : null,
          valid: queryData.mx.valid || false
        } : null,
        spf: queryData.spf ? {
          status: queryData.spf.valid ? 'success' : queryData.spf.message?.includes('UYARI') ? 'warning' : 'error',
          message: queryData.spf.message || null,
          records: Array.isArray(queryData.spf.records) ? queryData.spf.records : null,
          valid: queryData.spf.valid || false
        } : null,
        dkim: queryData.dkim ? {
          status: queryData.dkim.valid ? 'success' : queryData.dkim.message?.includes('UYARI') ? 'warning' : 'error',
          message: queryData.dkim.message || null,
          records: Array.isArray(queryData.dkim.records) ? queryData.dkim.records : null,
          valid: queryData.dkim.valid || false
        } : null,
        dmarc: queryData.dmarc ? {
          status: queryData.dmarc.valid ? 'success' : queryData.dmarc.message?.includes('UYARI') ? 'warning' : 'error',
          message: queryData.dmarc.message || null,
          record: queryData.dmarc.record || null,
          valid: queryData.dmarc.valid || false,
          details: Array.isArray(queryData.dmarc.details) ? queryData.dmarc.details : null
        } : null
      };
    }
    // DNS sorgusu için özel işlemler
    else if (queryData.queryType === 'dns') {
      // İç içe dizileri düzleştir
      const flattenArrays = (data) => {
        if (Array.isArray(data)) {
          // İç içe dizi kontrolü
          if (data.some(item => Array.isArray(item))) {
            // İç içe dizileri düzleştir
            return data.map(item => 
              Array.isArray(item) ? item.join(', ') : item
            );
          }
          return data;
        }
        return data;
      };

      // DNS kayıtlarını düzleştir
      const processedData = {};
      for (const key in queryData) {
        if (key !== 'domain' && key !== 'queryType' && key !== 'timestamp' && key !== 'lastChecked') {
          if (key === 'DKIM' && typeof queryData[key] === 'object' && !Array.isArray(queryData[key])) {
            // DKIM özel işleme
            const processedDKIM = {};
            for (const selector in queryData[key]) {
              processedDKIM[selector] = flattenArrays(queryData[key][selector]);
            }
            processedData[key] = processedDKIM;
          } else {
            processedData[key] = flattenArrays(queryData[key]);
          }
        }
      }

      cleanData = {
        ...cleanData,
        ...processedData
      };
    }
    // WHOIS sorgusu için özel işlemler
    else if (queryData.queryType === 'whois') {
      // WHOIS verilerini temizle ve Timestamp objelerini düzleştir
      let whoisData = queryData.whoisData || queryData;
      
      // Timestamp objelerini düzleştir
      whoisData = flattenTimestamps(whoisData);
      
      // Firestore'a kaydedilebilir bir formata dönüştür
      const serializedWhoisData = JSON.parse(JSON.stringify(whoisData));
      
      cleanData = {
        ...cleanData,
        whoisData: serializedWhoisData
      };
    }

    // Sorguyu kaydet
    const queryRef = doc(db, `users/${userId}/queryHistory`, queryData.domain);
    await setDoc(queryRef, cleanData, { merge: true });

    return cleanData;
  } catch (error) {
    console.error("Sorgu kaydedilirken hata:", error);
    throw error;
  }
};

// Tüm sorguları getir
export const getAllQueries = async (userId) => {
  try {
    const queriesRef = collection(db, `users/${userId}/queryHistory`);
    const q = query(queriesRef, orderBy("timestamp", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        domain: doc.id,
        timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : new Date(data.timestamp),
        lastChecked: data.lastChecked instanceof Timestamp ? data.lastChecked.toDate() : new Date(data.lastChecked)
      };
    });
  } catch (error) {
    console.error("Sorgular getirilirken hata:", error);
    throw error;
  }
};

// Sorguyu sil
export const deleteQuery = async (userId, domain) => {
  try {
    const queryRef = doc(db, `users/${userId}/queryHistory`, domain);
    await deleteDoc(queryRef);
    return true;
  } catch (error) {
    console.error("Sorgu silinirken hata:", error);
    throw error;
  }
};

// Sorguyu takip et
export const followQuery = async (userId, query) => {
  try {
    const followRef = doc(db, `users/${userId}/followedQueries`, query.domain);
    await setDoc(followRef, {
      ...query,
      followedAt: Timestamp.fromDate(new Date())
    });
    return true;
  } catch (error) {
    console.error("Sorgu takip edilirken hata:", error);
    throw error;
  }
};

// Takibi bırak
export const unfollowQuery = async (userId, domain) => {
  try {
    const followRef = doc(db, `users/${userId}/followedQueries`, domain);
    await deleteDoc(followRef);
    return true;
  } catch (error) {
    console.error("Sorgu takibi bırakılırken hata:", error);
    throw error;
  }
};

// Takip edilen sorguları getir
export const getFollowedQueries = async (userId) => {
  try {
    const followedRef = collection(db, `users/${userId}/followedQueries`);
    const q = query(followedRef, orderBy("followedAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        domain: doc.id,
        timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : new Date(data.timestamp),
        lastChecked: data.lastChecked instanceof Timestamp ? data.lastChecked.toDate() : new Date(data.lastChecked),
        followedAt: data.followedAt instanceof Timestamp ? data.followedAt.toDate() : new Date(data.followedAt),
        isFollowed: true
      };
    });
  } catch (error) {
    console.error("Takip edilen sorgular getirilirken hata:", error);
    throw error;
  }
};

// Sorguyu yenile
export const refreshQuery = async (userId, domain) => {
  try {
    const token = await getIdToken();
    if (!token) throw new Error('Kullanıcı girişi gerekli');

    const result = await handleQuery(domain, 'gmail', token);
    
    // Sorguyu güncelle
    const now = new Date();
    await saveQuery(userId, {
      domain,
      ...result,
      queryType: 'gmail',
      timestamp: now,
      lastChecked: now
    });

    return result;
  } catch (error) {
    console.error("Sorgu yenilenirken hata:", error);
    throw error;
  }
};
